import {
  Configuration,
  ActivitiesApi,
  AuthApi,
  BazaarApi,
  CarContributorsApi,
  CarsApi,
  ConversationsApi,
  EndpointsApi,
  EventsApi,
  ForumApi,
  MessagesApi,
  NotificationsApi,
  UsersApi,
} from './generated';
import type { Middleware, RequestContext, ResponseContext } from './generated';
import i18n from '../i18n';

const TOKEN_KEY = 'stututu_token';

/**
 * Largest request body the server accepts. Must match nginx-proxy's
 * client_max_body_size (deploy/nginx-proxy/upload_limit.conf) and PHP's
 * post_max_size (base/docker/php/uploads.ini). The proxy's 413 carries no
 * CORS headers, so the browser only reports "Load failed" — check first.
 */
const UPLOAD_MAX_MB = 25;
export const UPLOAD_MAX_BYTES = UPLOAD_MAX_MB * 1024 * 1024;

function formDataBytes(form: FormData): number {
  let total = 0;
  form.forEach((value) => {
    if (value instanceof Blob) total += value.size;
  });
  return total;
}

export const tokenStorage = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (token: string) => localStorage.setItem(TOKEN_KEY, token),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

const LOCALE_KEY = 'stututu_locale';

function currentLocale(): string {
  return localStorage.getItem(LOCALE_KEY) ?? 'en';
}

export const API_BASE_URL = (process.env.REACT_APP_API_URL ?? 'http://api.stututu.local').replace(/\/+$/, '');

const authMiddleware: Middleware = {
  pre: async (context: RequestContext) => {
    const headers = new Headers(context.init.headers);
    headers.set('Accept', 'application/json');
    headers.set('Accept-Language', currentLocale());
    const token = tokenStorage.get();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return {
      url: context.url,
      init: { ...context.init, headers },
    };
  },
  post: async (context: ResponseContext) => {
    // Only treat 401 as session expiry when we actually had a token. Public
    // pages can call protected endpoints (e.g. notifications) for guests —
    // those 401s should fail silently, not redirect to /login.
    if (context.response.status === 401 && tokenStorage.get()) {
      tokenStorage.clear();
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    }
    return context.response;
  },
};

const config = new Configuration({
  basePath: API_BASE_URL,
  middleware: [authMiddleware],
});

export const authApi = new AuthApi(config);
export const usersApi = new UsersApi(config);
export const carsApi = new CarsApi(config);
export const activitiesApi = new ActivitiesApi(config);
export const eventsApi = new EventsApi(config);
export const bazaarApi = new BazaarApi(config);
export const forumApi = new ForumApi(config);
export const conversationsApi = new ConversationsApi(config);
export const messagesApi = new MessagesApi(config);
export const notificationsApi = new NotificationsApi(config);
export const carContributorsApi = new CarContributorsApi(config);
export const endpointsApi = new EndpointsApi(config);

/**
 * Until Scribe supports response schema introspection, generated methods all
 * return `Promise<void>`. We expose a low-level fetch helper for reading the
 * actual JSON shape. Use sparingly — prefer the generated APIs for *requests*.
 */
export async function apiFetch<T = any>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  headers.set('Accept-Language', currentLocale());
  const isFormData = init.body instanceof FormData;
  if (init.body && !headers.has('Content-Type') && !isFormData) {
    headers.set('Content-Type', 'application/json');
  }
  const token = tokenStorage.get();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  // If the base URL already ends with /api/v1 (prod) and the caller passes a
  // /api/v1-prefixed path, drop the duplicate. Keeps the 60+ existing call
  // sites working under both prod (REACT_APP_API_URL=https://host/api/v1) and
  // dev (REACT_APP_API_URL=http://host).
  const normalizedPath = API_BASE_URL.endsWith('/api/v1') && path.startsWith('/api/v1/')
    ? path.slice('/api/v1'.length)
    : path;
  if (isFormData && formDataBytes(init.body as FormData) > UPLOAD_MAX_BYTES) {
    throw new Error(i18n.t('errors.uploadTooLarge', { maxMb: UPLOAD_MAX_MB }));
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${normalizedPath}`, {
      ...init,
      headers,
    });
  } catch {
    // Browsers hide the reason ("Load failed", "Failed to fetch").
    throw new Error(i18n.t(isFormData ? 'errors.uploadFailed' : 'errors.network'));
  }

  if (response.status === 413) {
    throw new Error(i18n.t('errors.uploadTooLarge', { maxMb: UPLOAD_MAX_MB }));
  }

  if (response.status === 401 && token) {
    tokenStorage.clear();
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
      window.location.href = '/login';
    }
  }

  if (!response.ok) {
    const text = await response.text();
    let message = response.statusText;
    try {
      const parsed = JSON.parse(text);
      message = parsed.message ?? message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}
